// src/pages/Colostro/ColostroPage.jsx
import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { cowHead } from '@lucide/lab';
import { formatarDataHora, formatarLog10 } from '../../utils/coleta';
import { AZUL_CLARO } from '../../theme/cores';
import {
    REGISTROS_DEMONSTRACAO,
    avaliarBrix,
    avaliarCbt,
    minutosAteFornecimento,
} from './colostro';
import SerialDispositivo from '../Coleta/components/SerialDispositivo';
import { MARGEM_LATERAL, TITULO_PAGINA } from '../../theme/escalaPagina';

const AMBAR = '#E0A43A';
const UMA_CASA = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const DECIMAL = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const SEM_ANALISE = 'Disponível quando a análise de colostro estiver habilitada nos dispositivos.';

const IconeVaca = ({ size = 20 }) => <Lucide.Icon iconNode={cowHead} size={size} />;
const dataHora = (iso) => formatarDataHora(new Date(iso));

const duracao = (minutos) => {
    if (minutos == null) return '';
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
};

function Rotulo({ cor, children }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cor }} />
            <Typography variant="caption" color="text.secondary">{children}</Typography>
        </Stack>
    );
}

function Info({ rotulo, valor }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{rotulo}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{valor}</Typography>
        </Box>
    );
}

/** Linha do tempo do registro: parto → coleta → fornecimento → resultado. */
function LinhaDoTempo({ registro }) {
    const minutos = minutosAteFornecimento(registro);
    const etapas = [
        { titulo: 'Parto', quando: registro.parto },
        { titulo: 'Coleta', quando: registro.coleta },
        {
            titulo: 'Fornecimento',
            quando: registro.fornecimento,
            detalhe: minutos != null ? `${duracao(minutos)} após o parto` : null,
        },
        { titulo: 'Resultado microbiológico', quando: registro.resultado },
    ];
    return (
        <Stack sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
            {etapas.map((e, i) => (
                <Stack key={e.titulo} direction="row" spacing={1.5}>
                    <Stack alignItems="center" sx={{ width: 24 }}>
                        <Box
                            sx={{
                                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(AZUL_CLARO, 0.12), color: AZUL_CLARO,
                            }}
                        >
                            <Lucide.Check size={14} strokeWidth={3} />
                        </Box>
                        {i < etapas.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 18, bgcolor: 'divider' }} />}
                    </Stack>
                    <Box sx={{ pb: i < etapas.length - 1 ? 1.75 : 0 }}>
                        <Typography variant="body2" fontWeight={700}>{e.titulo}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {dataHora(e.quando)}
                            {e.detalhe ? ` · ${e.detalhe}` : ''}
                        </Typography>
                    </Box>
                </Stack>
            ))}
        </Stack>
    );
}

function RegistroCard({ registro }) {
    const theme = useTheme();
    const brix = avaliarBrix(registro.brix);
    const cbt = avaliarCbt(registro.cbtUfcMl);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(AMBAR, 0.14), color: AMBAR, display: 'flex', flexShrink: 0 }}>
                    <IconeVaca />
                </Box>
                <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                    <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{registro.codigo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {registro.vaca} · {registro.bezerra}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Chip size="small" label="Exemplo" variant="outlined" sx={{ fontWeight: 600 }} />
                    <Chip size="small" color="success" variant="outlined" icon={<Lucide.CircleCheck size={14} />} label="Concluída" sx={{ fontWeight: 600 }} />
                </Stack>
            </Stack>

            <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.75 }}>
                <SerialDispositivo serial={registro.numeroSerie} compacto />
            </Box>
            <Grid container spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 1.5 } }}>
                <Grid item xs={6}>
                    <Rotulo cor={theme.palette.text.disabled}>Registro do produtor</Rotulo>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>Leitura de Brix</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                        <Typography fontWeight={800} sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>{UMA_CASA.format(registro.brix)}</Typography>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">°Brix</Typography>
                    </Stack>
                    {brix && <Typography variant="caption" color={`${brix.tom}.main`} fontWeight={600}>{brix.rotulo}</Typography>}
                </Grid>
                <Grid item xs={6}>
                    <Rotulo cor={AZUL_CLARO}>Resultado do ensaio · exemplo</Rotulo>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>CBT</Typography>
                    <Typography fontWeight={800} sx={{ fontSize: '1.35rem', lineHeight: 1.2 }}>{INTEIRO.format(registro.cbtUfcMl)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        UFC/mL · {formatarLog10(Math.log10(registro.cbtUfcMl))}
                    </Typography>
                    {cbt && <Typography variant="caption" color={`${cbt.tom}.main`} fontWeight={600}>{cbt.rotulo}</Typography>}
                </Grid>
            </Grid>

            {/* Manejo: mesmo fundo do card, separado por linhas (fundo cinza destoava). */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}><Info rotulo="Armazenamento" valor={registro.armazenamento} /></Grid>
                    <Grid item xs={6} sm={4}><Info rotulo="Recipiente" valor={registro.recipiente} /></Grid>
                    <Grid item xs={6} sm={4}><Info rotulo="Volume fornecido" valor={`${DECIMAL.format(registro.volumeL)} L`} /></Grid>
                </Grid>
            </Box>

            <LinhaDoTempo registro={registro} />

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center" sx={{ p: 2, mt: 'auto', borderTop: 1, borderColor: 'divider' }}>
                <Tooltip title={SEM_ANALISE}>
                    <span>
                        <Button disabled sx={{ fontWeight: 700 }}>Atualizar manejo</Button>
                    </span>
                </Tooltip>
                <Tooltip title={SEM_ANALISE}>
                    <span>
                        <Button variant="outlined" color="inherit" disabled startIcon={<Lucide.Eye size={16} />} sx={{ borderRadius: 2, fontWeight: 600 }}>
                            Ver amostra
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
        </Paper>
    );
}

/**
 * Qualidade do colostro — DEMONSTRAÇÃO. O colostro se avalia por Brix (IgG, medido pelo
 * produtor), pela contagem bacteriana e pelo manejo do fornecimento. Como ainda não há
 * análise de colostro nos dispositivos, a página apresenta o fluxo com registros de
 * exemplo, marcados como tal (ver ./colostro.js).
 */
export default function ColostroPage() {
    const [busca, setBusca] = useState('');
    const registros = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return REGISTROS_DEMONSTRACAO;
        return REGISTROS_DEMONSTRACAO.filter((r) =>
            [r.codigo, r.vaca, r.bezerra].some((t) => t.toLowerCase().includes(termo))
        );
    }, [busca]);

    return (
        <Box sx={{ px: MARGEM_LATERAL, pt: { xs: 2, xl: 3 }, pb: { xs: 3, md: 4 }, maxWidth: 1500, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                        Controle microbiológico
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={TITULO_PAGINA}>Qualidade do colostro</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Acompanhe a amostra e o manejo, da coleta ao fornecimento.
                    </Typography>
                </Box>
                <Tooltip title={SEM_ANALISE}>
                    <span>
                        <Button variant="contained" disableElevation disabled startIcon={<Lucide.Plus size={18} />} sx={{ borderRadius: 2, fontWeight: 700 }}>
                            Nova análise
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Alert severity="info" icon={<Lucide.Info size={20} />} sx={{ mt: 3, borderRadius: 2 }}>
                <strong>Demonstração.</strong> A análise de colostro ainda não está disponível nos dispositivos. Os registros
                abaixo são exemplos para mostrar como o acompanhamento vai funcionar.
            </Alert>

            <Paper
                variant="outlined"
                sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: alpha(AMBAR, 0.08), borderColor: alpha(AMBAR, 0.3) }}
            >
                <Stack direction="row" spacing={1.5}>
                    <Box sx={{ color: 'text.secondary', mt: 0.25 }}><IconeVaca /></Box>
                    <Box>
                        <Typography fontWeight={700}>O fornecimento tem seu próprio tempo</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Registre a alimentação quando ela ocorrer. O resultado microbiológico pode chegar depois e não deve
                            atrasar o fornecimento de colostro à bezerra recém-nascida — o ideal é oferecer cerca de 10% do peso
                            vivo (~4 L) o quanto antes, até no máximo 6 horas após o parto.
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mt: 4, mb: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" fontWeight={700}>Registros de colostro</Typography>
                    <Chip size="small" label={registros.length} sx={{ height: 22, fontWeight: 700 }} />
                </Stack>
                <TextField
                    size="small"
                    placeholder="Buscar vaca, bezerra ou amostra"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    sx={{ width: { xs: '100%', md: 320 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lucide.Search size={18} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {registros.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>Nenhum registro encontrado.</Typography>
            ) : (
                <Grid container spacing={2.5} alignItems="stretch">
                    {registros.map((r) => (
                        <Grid item xs={12} md={6} key={r.id}>
                            <RegistroCard registro={r} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>
                Referências: Brix ≥ 22% indica colostro de alta qualidade (IgG ≈ 50 g/L ou mais); contagem bacteriana total
                abaixo de 100.000 UFC/mL.
            </Typography>
        </Box>
    );
}
